import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { productsAPI, ordersAPI, categoriesAPI } from '../utils/api';
import { toast } from 'sonner';
import OrderManagement from '../components/admin/OrderManagement';
import ProductManagement from '../components/admin/ProductManagement';
import ReviewManagement from '../components/admin/ReviewManagement';

const Admin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (user && user.role !== 'admin') navigate('/');
    else if (!user || user === false) navigate('/login');
  }, [user, loading, navigate]);

  const loadData = useCallback(async () => {
    try {
      if (activeTab === 'products') {
        const { data } = await productsAPI.getAll({});
        setProducts(data);
      } else if (activeTab === 'orders') {
        const { data } = await ordersAPI.getAll();
        setOrders(data);
      } else if (activeTab === 'categories') {
        const { data } = await categoriesAPI.getAll();
        setCategories(data);
      }
    } catch (_) {
      toast.error('Failed to load data');
    }
  }, [activeTab]);

  useEffect(() => {
    if (user && user.role === 'admin') loadData();
  }, [user, loadData]);

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="admin-unauthorized">
        <p className="font-outfit text-[#8C8577]">Unauthorized. Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#F3EFE6]" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-cormorant text-4xl sm:text-5xl mb-8" data-testid="admin-heading">Admin Panel</h1>

        <div className="flex gap-4 mb-8 border-b border-[#EBE4D5]" data-testid="admin-tabs">
          {['orders', 'products', 'reviews', 'categories'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-4 px-4 font-outfit uppercase tracking-wider text-sm ${activeTab === tab ? 'border-b-2 border-[#C29B57] text-[#C29B57]' : 'text-[#8C8577]'}`} data-testid={`tab-${tab}`}>
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'orders' && <OrderManagement orders={orders} onRefresh={loadData} />}
        {activeTab === 'products' && <ProductManagement products={products} onRefresh={loadData} />}
        {activeTab === 'reviews' && <ReviewManagement />}
        {activeTab === 'categories' && (
          <div data-testid="categories-tab">
            <h2 className="font-cormorant text-2xl mb-6">Categories</h2>
            <div className="bg-white rounded-lg shadow-sm p-6">
              {categories.map(cat => (
                <div key={cat.id} className="flex justify-between items-center py-3 border-b border-[#EBE4D5] last:border-0">
                  <span className="font-outfit text-sm">{cat.name} ({cat.slug})</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
